import BlockApi from '@/api/api';
import { useApplication } from '@/hooks/useApplication';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { Release, ReleaseTarget } from '@/types/backend';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import RevisionListItem from './RevisionListItem';

type PublishApplicationDialogProps = {
  open: boolean;
  onClose: () => void;
};

const PublishApplicationDialog: FC<PublishApplicationDialogProps> = (props) => {
  const auth = useAuth();
  const { config } = useConfig();
  const { backendApplication } = useApplication();
  const { showError } = useDialogs();

  const [releaseAll, setReleaseAll] = useState<Release>();
  const [releaseWhitelist, setReleaseWhitelist] = useState<Release>();
  const [history, setHistory] = useState<Release[]>();

  const getReleases = async () => {
    if (!auth.user?.access_token || !(backendApplication && backendApplication.applicationId)) return;

    try {
      const resultWhitelist = await BlockApi(config.apiUrl, auth.user.access_token).release.current(
        ReleaseTarget.Whitelist,
        backendApplication.applicationId
      );

      setReleaseWhitelist(resultWhitelist[0]);
      console.debug('releases whitelist', resultWhitelist);

      const resultAll = await BlockApi(config.apiUrl, auth.user.access_token).release.current(
        ReleaseTarget.All,
        backendApplication.applicationId
      );
      setReleaseAll(resultAll[0]);
      console.debug('releases all', resultAll);
    } catch (error) {
      showError({
        title: 'Error while getting releases',
        message: 'Something went wrong while getting the current releases.',
        stacktrace: error
      });
    }
  };

  const getHistory = async () => {
    if (!auth.user || !(backendApplication && backendApplication.applicationId)) return;
    const bHistory = await BlockApi(config.apiUrl, auth.user.access_token).release.history(
      backendApplication.applicationId
    );
    setHistory(bHistory);
  };

  const updateData = () => {
    // get releases
    getReleases();
    // get release history
    // TODO: Uncomment getHistory when backend is updated with new data.
    // getHistory();
  };

  useEffect(() => {
    if (props.open) {
      updateData();
    }
  }, [props.open]);

  const handleOnChange = () => {
    updateData();
  };

  const whitelistHistory = history?.filter((h) => h.releaseTargetId === ReleaseTarget.Whitelist);
  const allHistory = history?.filter((h) => h.releaseTargetId === ReleaseTarget.All);

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth='xs'>
      <DialogTitle>Publish Application</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DialogContentText>
          Current revision: <code>{backendApplication ? backendApplication.revision : 'unknown'}</code>
        </DialogContentText>
        <DialogContentText>Published revisions:</DialogContentText>
        <RevisionListItem
          currentApplicationId={backendApplication?.applicationId}
          currentRevision={backendApplication?.revision}
          appRevision={releaseAll}
          target={ReleaseTarget.All}
          history={allHistory}
          onChange={handleOnChange}
        />
        <RevisionListItem
          currentApplicationId={backendApplication?.applicationId}
          currentRevision={backendApplication?.revision}
          appRevision={releaseWhitelist}
          target={ReleaseTarget.Whitelist}
          history={whitelistHistory}
          onChange={handleOnChange}
        />
      </DialogContent>
      <DialogActions
        sx={
          {
            // TODO: Uncomment this style when backend is updated with new data.
            // justifyContent: 'space-between',
          }
        }
      >
        {/* 
        // TODO: Uncomment this button when backend is updated with new data.
        <Button variant="text" color="primary">
          History
        </Button> */}
        <Button onClick={props.onClose} color='primary'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishApplicationDialog;
